import { makeAutoObservable } from "mobx";
import { Quantity } from "../../core";
import { ModifierItemPackagingVariantId } from "../Identifiers";
import { ModifierItemInstance } from "../ProductInstance";
export class ModifierItemInstanceCreator {
    constructor(params) {
        this.modifierItemInstanceAvailabilityService =
            params.modifierItemInstanceAvailabilityService;
        this.itemQuantities = params.itemQuantities;
        this.modifierType = params.modifierType;
        this.modifierItemType = params.modifierItemType;
        this.modifierItemPackagingFeeId = params.modifierItemPackagingFeeId;
        this.maxDuplicatesPerItem = params.maxDuplicatesPerItem;
        this.creatorScopeProvider = params.creatorScopeProvider;
        this.defaultQuantity = params.defaultQuantity;
        this.editability = params.editability;
        makeAutoObservable(this);
    }
    get typeId() {
        return this.modifierItemType.id;
    }
    get variantKey() {
        return new ModifierItemPackagingVariantId(this.typeId, this.modifierItemPackagingFeeId);
    }
    get isIrremovableIngredient() {
        return this.modifierItemType.irremovable && this.isDefault;
    }
    get isPossiblyAvailable() {
        const availability = this.modifierItemInstanceAvailabilityService.unitAvailability(this, this.creatorScopeProvider.parameterSet);
        return availability.isAvailable.isPossible;
    }
    get quantity() {
        if (!this.isPossiblyAvailable) {
            return Quantity.Zero;
        }
        if (!this.editability.isPossiblyEditable) {
            return this.defaultQuantity;
        }
        return this.itemQuantities.quantity(this.variantKey);
    }
    get isEditable() {
        return this.editability.isPossiblyEditable && this.isPossiblyAvailable;
    }
    get isDefault() {
        return this.defaultQuantity.value > 0;
    }
    get canIncrement() {
        if (!this.isEditable) {
            return false;
        }
        if (this.maxDuplicatesPerItem !== null &&
            this.quantity.geq(this.maxDuplicatesPerItem)) {
            return false;
        }
        return true;
    }
    increment() {
        if (this.canIncrement) {
            this.itemQuantities.increment(this.variantKey);
        }
    }
    get canDecrement() {
        if (!this.isEditable) {
            return false;
        }
        if (this.modifierItemType.irremovable &&
            this.quantity.leq(this.defaultQuantity)) {
            return false;
        }
        return this.quantity.value > 0;
    }
    decrement() {
        if (this.canDecrement) {
            this.itemQuantities.decrement(this.variantKey);
        }
    }
    get isSelected() {
        return this.quantity.value > 0;
    }
    setSelected(selected) {
        if (!this.isEditable) {
            throw new Error(`ModifierItemInstance not editable`);
        }
        if (this.isIrremovableIngredient && !selected) {
            return;
        }
        this.itemQuantities.setQuantity(this.variantKey, selected ? Quantity.One : Quantity.Zero);
    }
    select() {
        if (!this.isEditable) {
            throw new Error(`ModifierItemInstance not editable`);
        }
        this.itemQuantities.clear();
        this.itemQuantities.setQuantity(this.variantKey, Quantity.One);
    }
    get modifierItemInstance() {
        return new ModifierItemInstance({
            modifierType: this.modifierType,
            modifierItemType: this.modifierItemType,
            defaultQuantity: this.defaultQuantity,
            quantity: this.quantity,
            modifierItemPackagingFeeId: this.modifierItemPackagingFeeId,
        });
    }
}
